import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Three states: null (unmarked) → present → absent → null
const nextStatus = (current) => {
  if (!current) return "present";
  if (current === "present") return "absent";
  return null;
};

const statusConfig = {
  present: { bg: "#f0fdf4", border: "#38a169", avatarBg: "#38a169", pillBg: "#d4f1e0", pillColor: "#1a6b3a", icon: "✓", label: "Present" },
  absent:  { bg: "#fff5f5", border: "#e53e3e", avatarBg: "#e53e3e", pillBg: "#fde8de", pillColor: "#9b1c1c", icon: "✗", label: "Absent" },
  null:    { bg: "white",   border: "#e8e0d0", avatarBg: "#d1d5db", pillBg: "#f0ebe0", pillColor: "#9ca3af", icon: null, label: "Unmarked" },
};

export default function Attendance({ academy }) {
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeBatch, setActiveBatch] = useState("All");
  const [view, setView] = useState("mark");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");

  const today = new Date();
  const dateStr = today.toISOString().split("T")[0];
  const batches = ["All", "Morning", "Evening", "Weekend", "U-14", "U-19"];

  const [userId, setUserId] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
    });
  }, []);

  useEffect(() => {
    if (userId) { fetchPlayers(); fetchTodayAttendance(); }
  }, [userId]);

  const fetchPlayers = async () => {
    const { data } = await supabase.from("players").select("*").eq("user_id", userId).order("name");
    setPlayers(data || []);
    setLoading(false);
  };

  const fetchTodayAttendance = async () => {
    const { data } = await supabase.from("attendance").select("*").eq("date", dateStr);
    if (data && data.length > 0) {
      const map = {};
      data.forEach((r) => { map[r.player_id] = r.status; });
      setAttendance(map);
      setSaved(true);
    }
  };

  const toggle = (playerId) => {
    if (saved) return;
    setAttendance((prev) => ({
      ...prev,
      [playerId]: nextStatus(prev[playerId]),
    }));
  };

  const markAll = (status) => {
    if (saved) return;
    const map = {};
    filtered.forEach((p) => { map[p.id] = status; });
    setAttendance((prev) => ({ ...prev, ...map }));
  };

  const clearAll = () => {
    if (saved) return;
    const map = {};
    filtered.forEach((p) => { map[p.id] = null; });
    setAttendance((prev) => ({ ...prev, ...map }));
  };

  const saveAttendance = async () => {
    if (!userId) { showToast("Error: not logged in"); return; }
    setSaving(true);
    // Only save explicitly marked players — skip unmarked
    const records = filtered
      .filter((p) => attendance[p.id] === "present" || attendance[p.id] === "absent")
      .map((p) => ({
        player_id: p.id,
        date: dateStr,
        status: attendance[p.id],
        user_id: userId,
      }));

    await supabase.from("attendance").delete().eq("date", dateStr).eq("user_id", userId);

    if (records.length > 0) {
      const { error } = await supabase.from("attendance").insert(records);
      if (error) { showToast("Error saving attendance"); setSaving(false); return; }
    }

    setSaved(true);
    showToast(`Saved · ${presentCount} present, ${absentCount} absent, ${unmarkedCount} unmarked`);
    setSaving(false);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const filtered = players.filter((p) => {
    const matchBatch = activeBatch === "All" || p.batch === activeBatch;
    const matchSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase());
    return matchBatch && matchSearch;
  });
  const presentCount = filtered.filter((p) => attendance[p.id] === "present").length;
  const absentCount = filtered.filter((p) => attendance[p.id] === "absent").length;
  const unmarkedCount = filtered.filter((p) => !attendance[p.id]).length;

  const getInitials = (name) =>
    name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <div style={s.container}>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.logoRow}>
          <div style={s.logoIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 17l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={s.logoText}>{academy?.name?.toUpperCase() || "THE COACH ROOM"}</span>
          <div style={s.viewToggle}>
            <button style={{ ...s.toggleBtn, background: view === "mark" ? "#d4a017" : "transparent", color: view === "mark" ? "#1a3a1b" : "rgba(255,255,255,0.5)" }} onClick={() => setView("mark")}>Today</button>
            <button style={{ ...s.toggleBtn, background: view === "history" ? "#d4a017" : "transparent", color: view === "history" ? "#1a3a1b" : "rgba(255,255,255,0.5)" }} onClick={() => setView("history")}>History</button>
          </div>
        </div>

        <div style={s.dateRow}>
          <div>
            <div style={s.dateMain}>{DAYS[today.getDay()]}, {today.getDate()} {MONTHS[today.getMonth()]}</div>
            <div style={s.dateSub}>{saved ? "✅ Saved" : "⏳ Not saved yet"}</div>
          </div>
          {saved && <button style={s.editBtn} onClick={() => setSaved(false)}>Edit</button>}
        </div>
      </div>

      {view === "mark" ? (
        <>
          {/* SUMMARY — 3 states */}
          <div style={s.summaryRow}>
            <div style={s.summaryCard}>
              <div style={{ ...s.summaryNum, color: "#38a169" }}>{presentCount}</div>
              <div style={s.summaryLabel}>Present</div>
            </div>
            <div style={s.summaryCard}>
              <div style={{ ...s.summaryNum, color: "#e53e3e" }}>{absentCount}</div>
              <div style={s.summaryLabel}>Absent</div>
            </div>
            <div style={{ ...s.summaryCard, borderRight: "none" }}>
              <div style={{ ...s.summaryNum, color: "#9ca3af" }}>{unmarkedCount}</div>
              <div style={s.summaryLabel}>Unmarked</div>
            </div>
          </div>

          {/* BATCH + QUICK ACTIONS */}
          <div style={s.controlRow}>
            <div style={s.batchScroll}>
              {batches.map((b) => (
                <button key={b} style={{ ...s.batchChip, background: activeBatch === b ? "#1a3a1b" : "white", color: activeBatch === b ? "#d4a017" : "#6b7280", border: activeBatch === b ? "1.5px solid #1a3a1b" : "1.5px solid #e8e0d0", fontWeight: activeBatch === b ? "700" : "400" }} onClick={() => setActiveBatch(b)}>{b}</button>
              ))}
            </div>
            {!saved && (
              <div style={s.markAllRow}>
                <button style={s.btnPresent} onClick={() => markAll("present")}>✓ All Present</button>
                <button style={s.btnAbsent} onClick={() => markAll("absent")}>✗ All Absent</button>
                <button style={s.btnClear} onClick={clearAll}>↺ Clear</button>
              </div>
            )}
          </div>

          {/* LEGEND */}
          {!saved && (
            <div style={s.legend}>
              <span style={s.legendItem}><span style={{ ...s.legendDot, background: "#9ca3af" }} />Unmarked</span>
              <span style={s.legendArrow}>→ tap →</span>
              <span style={s.legendItem}><span style={{ ...s.legendDot, background: "#38a169" }} />Present</span>
              <span style={s.legendArrow}>→ tap →</span>
              <span style={s.legendItem}><span style={{ ...s.legendDot, background: "#e53e3e" }} />Absent</span>
            </div>
          )}

          {/* SEARCH — always visible */}
          <div style={{ padding: "0.5rem 1.2rem 0", background: "#f0ebe0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", background: "white", border: "1.5px solid #e8e0d0", padding: "0.6rem 0.9rem" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8" stroke="#9ca3af" strokeWidth="2"/>
                <path d="M21 21l-4.35-4.35" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                style={{ flex: 1, border: "none", outline: "none", fontSize: "0.88rem", fontFamily: "-apple-system, sans-serif", background: "transparent", color: "#1a1a1a" }}
                placeholder="Search player name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "0.8rem", padding: 0 }} onClick={() => setSearch("")}>✕</button>
              )}
            </div>
          </div>

          {/* PLAYER LIST */}
          <div style={s.list}>
            {loading ? (
              <div style={s.empty}>Loading players...</div>
            ) : filtered.length === 0 ? (
              <div style={s.empty}>No players in this batch</div>
            ) : (
              filtered.map((player) => {
                const status = attendance[player.id] || null;
                const cfg = statusConfig[status] || statusConfig.null;
                return (
                  <button
                    key={player.id}
                    style={{ ...s.playerCard, background: cfg.bg, borderLeftColor: cfg.border, cursor: saved ? "default" : "pointer" }}
                    onClick={() => toggle(player.id)}
                  >
                    <div style={{ ...s.avatar, background: cfg.avatarBg }}>
                      {cfg.icon || getInitials(player.name)}
                    </div>
                    <div style={s.playerInfo}>
                      <div style={s.playerName}>{player.name}</div>
                      {player.batch && <div style={s.playerBatch}>{player.batch}</div>}
                    </div>
                    <div style={{ ...s.statusPill, background: cfg.pillBg, color: cfg.pillColor }}>
                      {cfg.label}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* SAVE */}
          {!saved && filtered.length > 0 && (
            <div style={s.saveWrap}>
              <button style={saving ? s.saveBtnDisabled : s.saveBtn} onClick={saveAttendance} disabled={saving}>
                {saving ? "Saving..." : `Save Attendance`}
              </button>
              {unmarkedCount > 0 && (
                <div style={s.saveNote}>{unmarkedCount} unmarked player{unmarkedCount > 1 ? "s" : ""} will not be recorded</div>
              )}
            </div>
          )}
        </>
      ) : (
        <HistoryView />
      )}

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}

// ─── HISTORY ──────────────────────────────────────
function HistoryView() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("attendance")
        .select("*, players(name, batch)")
        .order("date", { ascending: false })
        .limit(200);
      setRecords(data || []);
      setLoading(false);
    };
    fetch();
  }, []);

  const grouped = records.reduce((acc, r) => {
    if (!acc[r.date]) acc[r.date] = [];
    acc[r.date].push(r);
    return acc;
  }, {});

  const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
  const formatDate = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  if (loading) return <div style={s.empty}>Loading history...</div>;
  if (dates.length === 0) return (
    <div style={s.empty}>
      <div style={{ fontSize: "2.5rem", marginBottom: "0.8rem" }}>📅</div>
      <div style={{ fontWeight: "700", color: "#1a3a1b", marginBottom: "0.4rem" }}>No history yet</div>
      <div style={{ fontSize: "0.82rem", color: "#6b7280" }}>Save today's attendance to see it here</div>
    </div>
  );

  return (
    <div style={{ padding: "1rem 1.2rem 5rem" }}>
      {dates.map((date) => {
        const recs = grouped[date];
        const present = recs.filter((r) => r.status === "present").length;
        const total = recs.length;
        const pct = total > 0 ? Math.round((present / total) * 100) : 0;
        return (
          <div key={date} style={s.historyCard}>
            <div style={s.historyHeader}>
              <div>
                <div style={s.historyDate}>{formatDate(date)}</div>
                <div style={s.historyMeta}>{total} marked · {present} present</div>
              </div>
              <div style={s.historyBadge}>
                <span style={{ color: "#38a169", fontWeight: "800", fontSize: "1.1rem" }}>{present}</span>
                <span style={{ color: "#9ca3af" }}> / {total}</span>
                <div style={{ fontSize: "0.62rem", color: "#9ca3af" }}>present</div>
              </div>
            </div>
            <div style={s.historyBar}><div style={{ ...s.historyFill, width: `${pct}%` }} /></div>
            <div style={{ fontSize: "0.65rem", color: "#9ca3af", marginBottom: "0.6rem" }}>{pct}% attendance</div>
            <div style={s.historyPlayers}>
              {recs.map((r) => (
                <span key={r.id} style={{ ...s.chip, background: r.status === "present" ? "#d4f1e0" : "#fde8de", color: r.status === "present" ? "#1a6b3a" : "#9b1c1c" }}>
                  {r.status === "present" ? "✓" : "✗"} {r.players?.name?.split(" ")[0] || "—"}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const s = {
  container: { flex: 1, overflowY: "auto", background: "#f0ebe0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", minHeight: "100%", position: "relative" },
  header: { background: "#111a12", padding: "1.4rem 1.5rem 1rem" },
  logoRow: { display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1rem" },
  logoIcon: { width: "32px", height: "32px", background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.4)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" },
  logoText: { color: "white", fontSize: "0.72rem", fontWeight: "800", letterSpacing: "3px", flex: 1 },
  viewToggle: { display: "flex", background: "rgba(255,255,255,0.08)", borderRadius: "6px", overflow: "hidden", flexShrink: 0 },
  toggleBtn: { padding: "0.3rem 0.8rem", border: "none", cursor: "pointer", fontSize: "0.72rem", fontWeight: "600", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", borderRadius: "4px", transition: "all 0.15s" },
  dateRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  dateMain: { color: "white", fontSize: "1rem", fontWeight: "700" },
  dateSub: { color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginTop: "0.2rem" },
  editBtn: { background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.3)", color: "#d4a017", padding: "0.35rem 0.8rem", fontSize: "0.75rem", fontWeight: "700", cursor: "pointer", borderRadius: "4px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  summaryRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", background: "white", borderBottom: "1px solid #e8e0d0" },
  summaryCard: { padding: "0.9rem 0.5rem", textAlign: "center", borderRight: "1px solid #e8e0d0" },
  summaryNum: { fontWeight: "800", fontSize: "1.8rem", lineHeight: 1 },
  summaryLabel: { fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "0.3rem", fontWeight: "500" },
  controlRow: { padding: "0.8rem 1.2rem", background: "#f0ebe0", display: "flex", flexDirection: "column", gap: "0.6rem" },
  batchScroll: { display: "flex", gap: "0.5rem", overflowX: "auto", scrollbarWidth: "none" },
  batchChip: { padding: "0.35rem 0.8rem", fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "0.3px", flexShrink: 0, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", transition: "all 0.15s" },
  markAllRow: { display: "flex", gap: "0.5rem" },
  btnPresent: { flex: 1, padding: "0.5rem", background: "#1a3a1b", color: "#d4a017", border: "none", fontSize: "0.72rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  btnAbsent: { flex: 1, padding: "0.5rem", background: "#fde8de", color: "#9b1c1c", border: "none", fontSize: "0.72rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  btnClear: { padding: "0.5rem 0.8rem", background: "#f0ebe0", color: "#6b7280", border: "1px solid #e8e0d0", fontSize: "0.72rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  legend: { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", padding: "0.4rem 1.2rem", fontSize: "0.68rem", color: "#9ca3af" },
  legendItem: { display: "flex", alignItems: "center", gap: "0.3rem", fontWeight: "600" },
  legendDot: { width: "8px", height: "8px", borderRadius: "50%", display: "inline-block" },
  legendArrow: { color: "#d1d5db", fontSize: "0.65rem" },
  list: { padding: "0.5rem 1.2rem 8rem" },
  playerCard: { width: "100%", border: "1px solid #e8e0d0", borderLeft: "3px solid", padding: "0.85rem 1rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.9rem", textAlign: "left", transition: "all 0.15s", boxSizing: "border-box", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  avatar: { width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "1rem", fontWeight: "800", flexShrink: 0, transition: "background 0.2s" },
  playerInfo: { flex: 1, minWidth: 0 },
  playerName: { fontSize: "0.92rem", fontWeight: "700", color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  playerBatch: { fontSize: "0.7rem", color: "#9ca3af", marginTop: "0.15rem" },
  statusPill: { fontSize: "0.72rem", fontWeight: "700", padding: "0.3rem 0.8rem", borderRadius: "20px", flexShrink: 0, transition: "all 0.2s" },
  saveWrap: { position: "fixed", bottom: "4.5rem", left: "50%", transform: "translateX(-50%)", width: "calc(100% - 2.4rem)", maxWidth: "436px", zIndex: 10 },
  saveBtn: { width: "100%", padding: "1rem", background: "#1a3a1b", color: "#d4a017", border: "none", fontSize: "0.9rem", fontWeight: "800", cursor: "pointer", letterSpacing: "0.5px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  saveBtnDisabled: { width: "100%", padding: "1rem", background: "#a0aec0", color: "white", border: "none", fontSize: "0.9rem", fontWeight: "800", cursor: "not-allowed", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  saveNote: { textAlign: "center", fontSize: "0.7rem", color: "#9ca3af", marginTop: "0.4rem", fontStyle: "italic" },
  empty: { textAlign: "center", padding: "3rem 2rem", color: "#6b7280", fontSize: "0.9rem" },
  toast: { position: "fixed", bottom: "5rem", left: "50%", transform: "translateX(-50%)", background: "#1a3a1b", color: "white", padding: "0.7rem 1.4rem", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "600", whiteSpace: "nowrap", zIndex: 100, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" },
  historyCard: { background: "white", border: "1px solid #e8e0d0", padding: "1rem", marginBottom: "0.8rem" },
  historyHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.6rem" },
  historyDate: { fontSize: "0.88rem", fontWeight: "700", color: "#1a1a1a" },
  historyMeta: { fontSize: "0.72rem", color: "#6b7280", marginTop: "0.15rem" },
  historyBadge: { textAlign: "right" },
  historyBar: { height: "4px", background: "#f0ebe0", borderRadius: "2px", overflow: "hidden", marginBottom: "0.3rem" },
  historyFill: { height: "100%", background: "linear-gradient(90deg, #1a3a1b, #38a169)", borderRadius: "2px" },
  historyPlayers: { display: "flex", flexWrap: "wrap", gap: "0.35rem" },
  chip: { fontSize: "0.65rem", fontWeight: "700", padding: "0.2rem 0.5rem" },
};